import SearchParams from "./searchParams";

export type NotificationNavEvent = {
  idea?: string;
  challenge?: string;
  group?: string;
  discussion?: string;
  threadId?: string;
  commentId?: string;
  comment?: string;
  calendar?: string;
  ownerIdea?: { challenge?: string; idea?: string };
};

export type NotificationNavSource = {
  notificationType?: string;
  idea?: string;
  challenge?: string;
  group?: string;
  discussion?: string;
  ideaBusinessProfile?: string;
  ownerChallenge?: { _id?: string };
  context?: Record<string, unknown>;
  events?: NotificationNavEvent[];
};

type NotificationLocationOptions = { pathname?: string };

const MENTIONED_ENTITY_PATHS: Record<
  string,
  { segment: string; id: keyof NotificationNavSource }
> = {
  mentionedDiscussionComment: { segment: "discussions", id: "discussion" },
  mentionedIdeaComment: { segment: "ideas", id: "idea" },
};

const SOURCE_ID_FIELDS = [
  "idea",
  "challenge",
  "group",
  "discussion",
  "ideaBusinessProfile",
] as const;

function parseJsonField<T>(value: unknown): T | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as T;
    } catch {
      return undefined;
    }
  }
  return value as T;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function pathWithSearch(
  path: string,
  params: Record<string, string | undefined>,
) {
  return `${path}${new SearchParams(params).toSafeString()}`;
}

function resolveIds(source: NotificationNavSource) {
  const events = source.events ?? [];
  const event = events[0] ?? {};

  return {
    events,
    ideaId: source.idea ?? event.idea,
    challengeId:
      source.challenge ?? event.challenge ?? source.ownerChallenge?._id,
    groupId: source.group ?? event.group,
    discussionId: source.discussion ?? event.discussion,
    threadId: event.threadId,
    commentId: event.commentId,
    comment: event.comment,
    calendarId: event.calendar,
    submittedChallengeId:
      source.challenge ?? event.challenge ?? event.ownerIdea?.challenge,
  };
}

export function parseNotificationNavSource(
  data: unknown,
): NotificationNavSource | null {
  const record = asRecord(data);
  if (
    !record?.notificationType ||
    typeof record.notificationType !== "string"
  ) {
    return null;
  }

  const source: NotificationNavSource = {
    notificationType: record.notificationType,
    events: parseJsonField<NotificationNavEvent[]>(record.events) ?? [],
  };

  for (const key of SOURCE_ID_FIELDS) {
    const value = record[key];
    if (typeof value === "string" && value) {
      source[key] = value;
    }
  }

  const context = parseJsonField<Record<string, unknown>>(record.context);
  if (context) {
    source.context = context;
  }

  const ownerChallenge = parseJsonField<{ _id?: string }>(
    record.ownerChallenge,
  );
  if (ownerChallenge) {
    source.ownerChallenge = ownerChallenge;
  }

  if (!Array.isArray(source.events)) {
    source.events = [];
  }

  return source;
}

export function getNotificationLocation(
  source: NotificationNavSource,
  options: NotificationLocationOptions = {},
): string | null {
  const { notificationType } = source;
  if (!notificationType) return null;

  const {
    events,
    ideaId,
    challengeId,
    groupId,
    discussionId,
    threadId,
    commentId,
    comment,
    calendarId,
    submittedChallengeId,
  } = resolveIds(source);

  switch (notificationType) {
    case "messaged":
      return threadId ? `/messages/${threadId}` : null;

    case "ideaBusinessProfileInvited":
      return pathWithSearch(options.pathname ?? "", {
        businessProfileId: source.ideaBusinessProfile,
      });

    case "ideaCommented":
    case "ideaCommentReply":
    case "reactedIdeaComment":
      return ideaId ?
          pathWithSearch(`/ideas/${ideaId}`, { comment: commentId })
        : null;

    case "collaboratorAdded":
    case "ideaVoted":
    case "ideaInvited":
    case "aiIdeaFeedback":
    case "aiIdeaAnonymityCheck":
      return ideaId ? `/ideas/${ideaId}` : null;

    case "stageChanged":
    case "closingSoon":
    case "challengeClosed":
    case "requestOpenChallenge":
    case "challengeTranslate":
      return challengeId ? `/challenges/${challengeId}` : null;

    case "aiChallengeFormat":
      return challengeId ? `/challenges/${challengeId}/settings/format` : null;

    case "challengeInnointelSearch":
      if (source.context?.searchId) {
        return `/innointel/searches/${source.context.searchId}`;
      }
      return challengeId ? `/challenges/${challengeId}` : null;

    case "challengeAddedToCalendarEvent":
      return challengeId ? `/challenges/${challengeId}/planner` : null;

    case "groupAddedToCalendarEvent":
      if (!groupId) return null;
      return calendarId ?
          `/groups/${groupId}/calendars/${calendarId}`
        : `/groups/${groupId}`;

    case "assessorAssigned":
      return challengeId ? `/challenges/${challengeId}/assessments` : null;

    case "ideaSubmitted":
    case "ideaDeadlineSet":
    case "ideaDeadlineApproaching":
      if (events.length > 1) {
        return submittedChallengeId ?
            `/challenges/${submittedChallengeId}/ideas`
          : null;
      }
      return ideaId ? `/ideas/${ideaId}` : null;

    case "reactedDiscussionComment":
    case "discussionCommentReply":
    case "discussionPostRequiresApproval":
    case "discussionPosted":
    case "reactedGroupDiscussion":
    case "repliedGroupDiscussion":
    case "mentionedGroupDiscussion":
      if (!discussionId) {
        return groupId ? `/groups/${groupId}` : null;
      }
      return pathWithSearch(`/discussions/${discussionId}`, {
        comment: commentId,
      });

    case "discussionPollVoted":
      return discussionId ?
          pathWithSearch(`/discussions/${discussionId}`, { comment: comment })
        : null;

    case "mentionedDiscussionComment":
    case "mentionedIdeaComment": {
      const mentioned = MENTIONED_ENTITY_PATHS[notificationType];
      const entityId = mentioned ? source[mentioned.id] : undefined;
      if (!mentioned || !entityId || typeof entityId !== "string") {
        return null;
      }
      return pathWithSearch(`/${mentioned.segment}/${entityId}`, {
        comment: commentId,
      });
    }

    case "mentionedIdeaProjectComment":
    case "projectIdeaCommented":
    case "projectIdeaReplied":
      return challengeId ?
          pathWithSearch(`/challenges/${challengeId}/board`, {
            idea: ideaId,
            comment: commentId,
          })
        : null;

    case "projectIdeaMoved":
    case "projectAssignedAssignee":
    case "projectAssignedManager":
      return challengeId ?
          pathWithSearch(`/challenges/${challengeId}/board`, { idea: ideaId })
        : null;

    case "unlockedAchievement":
      return "/achievements";

    case "pollClosed":
      return source.context?.pollId ?
          `/admin/polls/${source.context.pollId}/results`
        : null;

    case "groupJoinRequested":
    case "groupInvited":
      return "/";

    case "groupUserJoined":
      return groupId ? `/groups/${groupId}` : null;

    case "announcementReport":
      return typeof source.context?.logLink === "string" ?
          source.context.logLink
        : null;

    default:
      return null;
  }
}
